import styled from 'styled-components';

import { ListCard } from 'components/list-card';
import { InfoCard } from 'components/info-card';
import { ListHeader } from 'components/list-header';

// Expenses uses 16px inside its transparent row border. Keep every section
// on that same text edge, including the shared border's device-pixel rounding.
const inset = '16px';
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;
export const Note = styled.div`
  border-radius: 8px;
  padding: ${inset};
  border: 1px solid transparent;
  background: ${({ theme }) => theme.colors.blue.tertiary.standard};
  line-height: 1.5;
  overflow-wrap: anywhere;
`;
export const Totals = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    > :last-child {
      grid-column: 1 / -1;
    }
  }
`;
export const SummaryCard = styled(InfoCard)`
  min-width: 0;
  padding: ${inset};
  border: 1px solid transparent;
  h3 {
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;
export const SectionText = styled.p`
  padding: 0 ${inset};
  border-inline: 1px solid transparent;
`;
export const Details = styled.dl`
  margin: 0;
  display: grid;
  gap: 8px;
  dt {
    font-weight: 600;
  }
  dd {
    margin: 0 0 16px;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }
`;
export const Form = styled.form`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;
  max-height: 75vh;
  overflow: auto;
  padding: 2px;
`;
export const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
export const SelectLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
  font-weight: 600;
  select {
    font: inherit;
    font-weight: 400;
    height: 40px;
    box-sizing: border-box;
    width: 100%;
    border: 1px solid #d9dfe8;
    border-radius: 8px;
    padding: 8px;
    background: white;
    color: inherit;
  }
`;
export const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
`;
export const Source = styled.span`
  white-space: normal;
  overflow-wrap: anywhere;
  min-width: 0;
`;
export const History = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #e7eaf0;
  summary {
    cursor: pointer;
  }
  p {
    margin: 8px 0;
    overflow-wrap: anywhere;
    line-height: 1.5;
  }
`;
export const ErrorText = styled.p`
  color: #b42318;
  line-height: 1.5;
  overflow-wrap: anywhere;
`;
export const Card = styled.fieldset`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: 1px solid #d9dfe8;
  border-radius: 8px;
  padding: 16px;
  min-width: 0;
  legend {
    padding: 0 8px;
  }
`;

export const RowActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  > button {
    width: 100%;
  }
`;

export const TenderTableContainer = styled.div`
  max-width: 100%;
  overflow-x: auto;
  h2 {
    padding: 0 ${inset};
    border-inline: 1px solid transparent;
  }
`;
export const TenderTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid transparent;
  font: inherit;
  font-variant-numeric: tabular-nums;
  margin-top: 16px;
  th,
  td {
    padding: 10px 8px;
    border-bottom: 1px solid #e7eaf0;
    text-align: right;
    white-space: nowrap;
  }
  thead {
    font-weight: 600;
    color: ${({ theme }) => theme.config.table.head.title};
  }
  th:first-child {
    text-align: left;
    padding-left: ${inset};
  }
  th:last-child,
  td:last-child {
    padding-right: ${inset};
  }
  @media (max-width: 600px) {
    display: block;
    border: 0;
    colgroup,
    thead {
      display: none;
    }
    tbody {
      display: grid;
      gap: 12px;
    }
    tbody tr {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      border: 1px solid #e7eaf0;
      border-radius: 8px;
      padding: 12px ${inset};
      gap: 12px;
    }
    tbody th:first-child {
      grid-column: 1 / -1;
      border: 0;
      padding: 0;
    }
    td {
      display: flex;
      flex-direction: column;
      border: 0;
      padding: 0;
      min-width: 0;
    }
    td::before {
      content: attr(data-label);
      font-size: 12px;
      color: ${({ theme }) => theme.config.table.head.title};
    }
    td:last-child {
      grid-column: 1 / -1;
      padding: 0;
    }
  }
`;
export const Money = styled.span`
  display: block;
  width: 100%;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;
// Put Total on the shared right edge without changing DOM/keyboard order.
const desktopColumns = [1, 2, 6, 7, 8, 9, 10, 3, 4, 5];
const desktopCellPositions = desktopColumns
  .map(
    (column, index) => `
  > :nth-child(${index + 1}) { grid-column: ${column}; grid-row: 1; }
`
  )
  .join('');
export const RowHeader = styled(ListHeader)`
  margin: 0;
  padding: 8px ${inset};
  ${desktopCellPositions}
  > :nth-child(n + 3):nth-child(-n + 7) {
    text-align: right;
  }
`;
export const RowCard = styled(ListCard)`
  > div {
    > :nth-child(n + 3):nth-child(-n + 7) {
      text-align: right;
      align-items: flex-end;
    }
    @media (min-width: 1200px) {
      ${desktopCellPositions}
    }
    @media (max-width: 1199px) {
      > :nth-child(7),
      > :nth-child(8) {
        grid-column: 1 / -1;
      }
      > :nth-child(9) {
        grid-column: span 2;
      }
      > :nth-child(10) {
        justify-self: end;
      }
    }
    @media (max-width: 600px) {
      > :nth-child(9) {
        grid-column: auto;
      }
    }
  }
`;
export const Truncated = styled.span`
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
`;
export const LoadedTag = styled(Truncated)`
  margin-top: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.blue.tertiary.standard};
  font-size: 11px;
`;

export const MenuButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.config.text.primary};
  border: 0;
  border-radius: 4px;
  min-width: 40px;
  min-height: 40px;
  font-size: 24px;
  cursor: pointer;
  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.colors.blue.tertiary.standard};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.blue.primary.standard};
  }
`;
